import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import moment from "moment";
import { Modal, Table, Tag, Alert } from "antd";
import { API_DOMAIN_MONITOR } from "../../config/config";

// Read-only list of a Domain Monitor user's related accounts or domains:
//   reseller → its merchants  (GET /v1/resellers/merchants?resellerId=)
//   client   → its resellers  (GET /v1/clients/resellers?clientId=)
//   any user → its domains    (GET /v1/domains?userId=; for a reseller/client, the domains of the merchants under it)
const PAGE_SIZE = 10;
const RELATION_CONFIG = {
    merchants: { path: "/v1/resellers/merchants", idParam: "resellerId", titleKey: "domain_monitor.users_merchants_of" },
    resellers: { path: "/v1/clients/resellers", idParam: "clientId", titleKey: "domain_monitor.users_resellers_of" },
    domains: { path: "/v1/domains", idParam: "userId", titleKey: "domain_monitor.users_domains_of" },
};
const STATUS_COLORS = { active: "success", inactive: "default", pending_verification: "warning" };
const MONITORING_COLORS = { active: "success", paused: "default" };

const UserRelationsModal = ({ t, visible, type, user, onClose }) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const config = RELATION_CONFIG[type];

    // Reset on close, so the next open starts on page 1 with an empty list and a single fetch
    // (the modal must close before another user/relation can be opened).
    useEffect(() => {
        if (!visible) {
            setPage(1);
            setRows([]);
            setTotal(0);
            setErrorMsg(null);
        }
    }, [visible]);

    useEffect(() => {
        if (!visible || !config || !user) return;
        let cancelled = false;

        const fetchRelations = async () => {
            const token = Cookies.get("dm_token");
            if (!token) {
                setErrorMsg(t("domain_monitor.no_dm_token"));
                return;
            }
            setIsLoading(true);
            setErrorMsg(null);
            try {
                const response = await axios.get(`${API_DOMAIN_MONITOR}${config.path}`, {
                    headers: { Authorization: "Bearer " + token },
                    params: { [config.idParam]: user.id, page, limit: PAGE_SIZE },
                });
                if (cancelled) return;
                const body = response.data || {};
                setRows(Array.isArray(body.data) ? body.data : []);
                setTotal(body.pagination && body.pagination.total !== undefined ? parseInt(body.pagination.total) || 0 : 0);
            } catch (error) {
                if (cancelled) return;
                setRows([]);
                setTotal(0);
                setErrorMsg(error.response?.data?.error?.message || error.message || t("domain_monitor.unexpected_error"));
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        fetchRelations();
        return () => {
            cancelled = true;
        };
    }, [visible, config, user, page, t]);

    const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "");

    const domainColumns = [
        { dataIndex: "domain", key: "domain", title: t("domain_monitor.domain_name"), ellipsis: true },
        { dataIndex: "name", key: "name", title: t("domain_monitor.business_name"), ellipsis: true },
        { dataIndex: "ownerName", key: "owner", title: t("domain_monitor.belongs_to"), ellipsis: true, render: (ownerName) => ownerName || "-" },
        {
            dataIndex: "status",
            key: "status",
            title: t("domain_monitor.status"),
            render: (status) => <Tag color={STATUS_COLORS[status] || "default"}>{t(`domain_monitor.${status}`, status)}</Tag>,
        },
        {
            dataIndex: "monitoringStatus",
            key: "monitoringStatus",
            title: t("domain_monitor.monitoring_status"),
            // Same as the Domains page switch: anything not active (including no monitoring row) is paused.
            render: (status) => {
                const shown = status === "active" ? "active" : "paused";
                return <Tag color={MONITORING_COLORS[shown]}>{t(`domain_monitor.${shown}`)}</Tag>;
            },
        },
        { dataIndex: "createdAt", key: "createdAt", title: t("domain_monitor.users_created_at"), render: formatDate },
    ];

    const userColumns = [
        { dataIndex: "email", key: "email", title: t("domain_monitor.users_email") },
        { dataIndex: "name", key: "name", title: t("domain_monitor.users_name") },
        {
            dataIndex: "status",
            key: "status",
            title: t("domain_monitor.users_status"),
            render: (status) => (
                <Tag color={STATUS_COLORS[status] || "default"}>{t(`domain_monitor.users_status_${status}`, status)}</Tag>
            ),
        },
        {
            dataIndex: "createdAt",
            key: "createdAt",
            title: t("domain_monitor.users_created_at"),
            render: formatDate,
        },
    ];

    const columns = type === "domains" ? domainColumns : userColumns;

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={type === "domains" ? 1000 : 800}
            destroyOnClose
            title={config && user ? t(config.titleKey, { name: user.name || user.email }) : ""}
        >
            {errorMsg && <Alert type="error" showIcon message={errorMsg} style={{ marginBottom: 16 }} />}
            <Table
                rowKey="id"
                size="small"
                loading={isLoading}
                columns={columns}
                dataSource={rows}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total,
                    showSizeChanger: false,
                    onChange: setPage,
                }}
            />
        </Modal>
    );
};

export default UserRelationsModal;
